import { useEffect, useState } from "react";
import TableInvoices from "../components/ui/tables/TableInvoices";
import { useButtonStore } from "../stores/components/button";
import { useInvoiceStore } from "../stores/invoice";
import {
  getInvoices,
  getInvoicesCSV,
  getInvoicesDocuments,
  getProformas,
} from "../api/invoices";
import Proforma from "../components/invoices/Proforma";
import { showToastError } from "../services/toast";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useLoaderStore } from "../stores/components/loader";
import { useUtilsStore } from "../stores/components/utils";
import { downloadCreditNote, getCreditNotes } from "../api/creditNotes";
import { useUserContext } from "../contexts/userContext";
import useModals from "../hooks/useModals";
import InvoiceGenerated from "../components/invoices/InvoiceGenerated";
import Totals from "../components/invoices/Totals";
import useSorting from "../contexts/useSorting";
import { getSortIcon, getSortButtonClasses } from "../utils/sortIcons";
import Loader from "../components/ui/Loader";
import ProgressBarLoader from "../components/ui/ProgressBarLoader";
import {
  ActionButton,
  PageHeader,
  PagePanel,
  PageShell,
  PanelCenter,
  PanelToolbar,
  PlusIcon,
  ResultCount,
  SearchField,
  SortBar,
  TabBar,
  ToolbarRow,
} from "../components/ui/page/PageShell";

const TITLE_BY_TAB: Record<string, string> = {
  Proformas: "Proformas",
  Avoirs: "Proformas d'avoirs",
  Factures: "Factures",
};

const ROW_LABEL_BY_TAB: Record<string, [string, string]> = {
  Proformas: ["proforma", "proformas"],
  Avoirs: ["avoir", "avoirs"],
  Factures: ["facture", "factures"],
};

const Invoices = () => {
  // Context
  const { role } = useUserContext();
  // Store
  const {
    tabInvoice,
    setTabInvoice,
    proformas,
    setProformas,
    idsProformasSelected,
    setIdsProformasSelected,
    viewOrUpdate,
    updateProforma,
    invoices,
    setInvoices,
    creditNotes,
    setCreditNotes,
    dataFilterProformas,
    setDataFilterProformas,
    dataFilterInvoices,
    setDataFilterInvoices,
    dataFilterCreditNotes,
    setDataFilterCreditNotes,
  } = useInvoiceStore();
  const { createButton, setCreateButton } = useButtonStore();
  const { isLoading, setIsLoading, isLoadingDocuments } = useLoaderStore();
  const { searchTerm, setSearchTerm } = useUtilsStore();
  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();
  // State
  const [loaderChargement, setLoaderChargement] = useState<boolean>(false);

  useEffect(() => {
    // Récupère les proformas
    if (tabInvoice === "Proformas" && !createButton) {
      setLoaderChargement(true);
      getProformas()
        .then((res) => {
          setProformas(res.data);
          setLoaderChargement(false);
        })
        .catch((err) => {
          setLoaderChargement(false);
          showToastError(err.response.data.message);
        });
    } else if (tabInvoice === "Factures" && !createButton) {
      // Récupère les factures
      setLoaderChargement(true);
      getInvoices()
        .then((res) => {
          setInvoices(res.data);
          setLoaderChargement(false);
        })
        .catch((err) => {
          setLoaderChargement(false);
          showToastError(err.response.data.message);
        });
    } else if (tabInvoice === "Avoirs" && !createButton) {
      // Récupère les avoirs
      setLoaderChargement(true);
      getCreditNotes()
        .then((res) => {
          setCreditNotes(res.data);
          setLoaderChargement(false);
        })
        .catch((err) => {
          setLoaderChargement(false);
          showToastError(err.response.data.message);
        });
    }
  }, [tabInvoice, createButton, viewOrUpdate, updateProforma]);

  const isCreatingOrUpdating = createButton || viewOrUpdate;

  const downloadAllDocuments = () => {
    setIsLoading(true);
    if (idsProformasSelected.length === 0) {
      showToastError("Veuillez sélectionner au moins une facture");
      setIsLoading(false);
      return;
    }
    // Télécharge les documents des avoirs
    if (tabInvoice === "Avoirs") {
      downloadCreditNote(Number(idsProformasSelected[0]))
        .then((response) => {
          setIsLoading(false);
          const url = window.URL.createObjectURL(
            new Blob([response.data], { type: "application/pdf" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `avoir_${name}`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          showToastError("Erreur lors du téléchargement du fichier");
          setIsLoading(false);
        });
    } else {
      // Télécharge les documents des factures
      getInvoicesDocuments(idsProformasSelected)
        .then((res) => {
          setIsLoading(false);
          const url = window.URL.createObjectURL(
            new Blob([res.data], { type: "application/pdf" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "proforma.pdf");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(() => {
          showToastError("Erreur lors du téléchargement des documents");
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    let data = [];
    // Filtre les données en fonction de la recherche pour les proformas
    if (tabInvoice === "Proformas") {
      data = proformas?.length > 0 ? [...proformas] : [...dataFilterProformas];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["customerFile"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["entityName"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["nameDepartment"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["amountExclVAT"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()),
        );
        setDataFilterProformas(data);
      } else {
        setDataFilterProformas(proformas);
      }
      // Filtre les données en fonction de la recherche pour les factures
    } else if (tabInvoice === "Factures") {
      data = invoices?.length > 0 ? [...invoices] : [...dataFilterInvoices];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["nameCustomerFile"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["invoiceNumber"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["commenditaire"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["nameDepartment"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["amountExclVAT"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["totalAmount"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["balance"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["issueDate"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (item["creditNotes"] ?? []).some(
              (cn: { creditNoteNumber?: string }) =>
                cn.creditNoteNumber
                  ?.toString()
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()),
            ),
        );
        setDataFilterInvoices(data);
      } else {
        setDataFilterInvoices(invoices);
      }
    } else if (tabInvoice === "Avoirs") {
      // Filtre les données en fonction de la recherche pour les avoirs
      data =
        creditNotes?.length > 0 ? [...creditNotes] : [...dataFilterCreditNotes];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["customerFile"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["invoiceNumber"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["nameDepartment"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["totalAmount"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()),
        );
        setDataFilterCreditNotes(data);
      } else {
        setDataFilterCreditNotes(creditNotes);
      }
    }
  }, [proformas, searchTerm, invoices, creditNotes]);

  // Hooks de tri pour chaque type de données
  const [
    sortedProformas,
    handleSortProformas,
    proformasSortField,
    proformasSortDirection,
  ] = useSorting(dataFilterProformas);
  const [
    sortedInvoices,
    handleSortInvoices,
    invoicesSortField,
    invoicesSortDirection,
  ] = useSorting(dataFilterInvoices);
  const [
    sortedCreditNotes,
    handleSortCreditNotes,
    creditNotesSortField,
    creditNotesSortDirection,
  ] = useSorting(dataFilterCreditNotes);

  // Fonction pour exporter les factures en CSV
  const handleExportCSV = () => {
    getInvoicesCSV()
      .then((res) => {
        const url = window.URL.createObjectURL(
          new Blob([res.data], { type: "application/csv" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "factures.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        showToastError("Erreur lors du téléchargement du fichier");
      });
  };

  const isDirection = role === "Admin" || role === "Direction";
  const tabs = isDirection
    ? [
        { id: "Proformas", label: "Proformas" },
        { id: "Factures", label: "Factures" },
        { id: "Avoirs", label: "Avoirs" },
      ]
    : [{ id: "Factures", label: "Factures" }];

  const activeData =
    tabInvoice === "Proformas"
      ? sortedProformas
      : tabInvoice === "Avoirs"
        ? sortedCreditNotes
        : sortedInvoices;
  const [singular, plural] = ROW_LABEL_BY_TAB[tabInvoice] ?? [
    "document",
    "documents",
  ];
  const selectedCount = idsProformasSelected.length;

  return (
    <PageShell>
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null,
      )}

      {isCreatingOrUpdating ? (
        <PagePanel>
          <Proforma />
        </PagePanel>
      ) : (
        <>
          <PageHeader
            eyebrow="Facturation"
            title={TITLE_BY_TAB[tabInvoice] ?? "Factures"}
            description={
              selectedCount > 0
                ? `${selectedCount} document${selectedCount > 1 ? "s" : ""} sélectionné${selectedCount > 1 ? "s" : ""}`
                : undefined
            }
            actions={
              <>
                {tabInvoice !== "Factures" && selectedCount > 0 && (
                  <ActionButton
                    variant="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(
                        "invoiceGenerated",
                        <InvoiceGenerated
                          closeModal={() => closeModal("invoiceGenerated")}
                        />,
                      );
                    }}
                  >
                    Générer en{" "}
                    {tabInvoice === "Proformas"
                      ? "factures"
                      : tabInvoice === "Avoirs"
                        ? "avoirs"
                        : null}
                  </ActionButton>
                )}

                {tabInvoice === "Factures" && (
                  <ActionButton onClick={() => handleExportCSV()}>
                    Export CSV
                  </ActionButton>
                )}

                <ActionButton onClick={downloadAllDocuments}>
                  Télécharger les documents
                </ActionButton>

                {tabInvoice === "Proformas" ? (
                  <ActionButton
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => setCreateButton(!createButton)}
                  >
                    Créer une proforma
                  </ActionButton>
                ) : tabInvoice === "Factures" &&
                  selectedCount === 1 &&
                  isDirection ? (
                  <ActionButton
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => setCreateButton(!createButton)}
                  >
                    Créer un avoir
                  </ActionButton>
                ) : null}
              </>
            }
          />

          <PagePanel>
            <PanelToolbar>
              <ToolbarRow>
                <TabBar
                  tabs={tabs}
                  value={tabInvoice}
                  onChange={(id) => {
                    setTabInvoice(id);
                    setIdsProformasSelected([]);
                  }}
                  className="sm:w-96"
                />
                <ResultCount
                  count={activeData?.length ?? 0}
                  singular={singular}
                  plural={plural}
                  filtered={searchTerm !== ""}
                />
              </ToolbarRow>

              <ToolbarRow>
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Rechercher un dossier, un numéro, un montant…"
                />

                {/* Filtres et tri */}
                <SortBar>
                  {tabInvoice === "Proformas" && (
                    <>
                      <button
                        onClick={() => handleSortProformas("customerFile")}
                        className={getSortButtonClasses(
                          "customerFile",
                          proformasSortField,
                        )}
                      >
                        <span>Dossier</span>
                        {getSortIcon(
                          "customerFile",
                          proformasSortField,
                          proformasSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortProformas("issueDate")}
                        className={getSortButtonClasses(
                          "issueDate",
                          proformasSortField,
                        )}
                      >
                        <span>Date</span>
                        {getSortIcon(
                          "issueDate",
                          proformasSortField,
                          proformasSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortProformas("amountExclVAT")}
                        className={getSortButtonClasses(
                          "amountExclVAT",
                          proformasSortField,
                        )}
                      >
                        <span>Montant HT</span>
                        {getSortIcon(
                          "amountExclVAT",
                          proformasSortField,
                          proformasSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortProformas("noBillingContacts")}
                        className={getSortButtonClasses(
                          "noBillingContacts",
                          proformasSortField,
                        )}
                      >
                        <span>Sans contact</span>
                        {getSortIcon(
                          "noBillingContacts",
                          proformasSortField,
                          proformasSortDirection,
                        )}
                      </button>
                    </>
                  )}

                  {tabInvoice === "Factures" && (
                    <>
                      <button
                        onClick={() => handleSortInvoices("invoiceNumber")}
                        className={getSortButtonClasses(
                          "invoiceNumber",
                          invoicesSortField,
                        )}
                      >
                        <span>N° facture</span>
                        {getSortIcon(
                          "invoiceNumber",
                          invoicesSortField,
                          invoicesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortInvoices("customerFile")}
                        className={getSortButtonClasses(
                          "customerFile",
                          invoicesSortField,
                        )}
                      >
                        <span>Dossier</span>
                        {getSortIcon(
                          "customerFile",
                          invoicesSortField,
                          invoicesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortInvoices("issueDate")}
                        className={getSortButtonClasses(
                          "issueDate",
                          invoicesSortField,
                        )}
                      >
                        <span>Date</span>
                        {getSortIcon(
                          "issueDate",
                          invoicesSortField,
                          invoicesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortInvoices("totalAmount")}
                        className={getSortButtonClasses(
                          "totalAmount",
                          invoicesSortField,
                        )}
                      >
                        <span>Montant TTC</span>
                        {getSortIcon(
                          "totalAmount",
                          invoicesSortField,
                          invoicesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortInvoices("balance")}
                        className={getSortButtonClasses(
                          "balance",
                          invoicesSortField,
                        )}
                      >
                        <span>Solde</span>
                        {getSortIcon(
                          "balance",
                          invoicesSortField,
                          invoicesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() =>
                          handleSortInvoices("daysAmountFromIssueDate")
                        }
                        className={getSortButtonClasses(
                          "daysAmountFromIssueDate",
                          invoicesSortField,
                        )}
                      >
                        <span>Relance</span>
                        {getSortIcon(
                          "daysAmountFromIssueDate",
                          invoicesSortField,
                          invoicesSortDirection,
                        )}
                      </button>
                    </>
                  )}

                  {tabInvoice === "Avoirs" && (
                    <>
                      <button
                        onClick={() => handleSortCreditNotes("invoiceNumber")}
                        className={getSortButtonClasses(
                          "invoiceNumber",
                          creditNotesSortField,
                        )}
                      >
                        <span>N° facture</span>
                        {getSortIcon(
                          "invoiceNumber",
                          creditNotesSortField,
                          creditNotesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortCreditNotes("issueDate")}
                        className={getSortButtonClasses(
                          "issueDate",
                          creditNotesSortField,
                        )}
                      >
                        <span>Date</span>
                        {getSortIcon(
                          "issueDate",
                          creditNotesSortField,
                          creditNotesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() => handleSortCreditNotes("totalAmount")}
                        className={getSortButtonClasses(
                          "totalAmount",
                          creditNotesSortField,
                        )}
                      >
                        <span>Montant TTC</span>
                        {getSortIcon(
                          "totalAmount",
                          creditNotesSortField,
                          creditNotesSortDirection,
                        )}
                      </button>
                      <button
                        onClick={() =>
                          handleSortCreditNotes("noBillingContacts")
                        }
                        className={getSortButtonClasses(
                          "noBillingContacts",
                          creditNotesSortField,
                        )}
                      >
                        <span>Sans contact</span>
                        {getSortIcon(
                          "noBillingContacts",
                          creditNotesSortField,
                          creditNotesSortDirection,
                        )}
                      </button>
                    </>
                  )}
                </SortBar>
              </ToolbarRow>
            </PanelToolbar>

            {/* Affichage du loader au chargement des données */}
            {loaderChargement ? (
              <PanelCenter>
                <LoadingSpinner size="large" text="Chargement des données..." />
              </PanelCenter>
            ) : isLoading ? (
              // Affichage du loader a la création
              <PanelCenter>
                <Loader />
              </PanelCenter>
            ) : isLoadingDocuments ? (
              <PanelCenter>
                <ProgressBarLoader
                  numberOfDocuments={idsProformasSelected.length}
                />
              </PanelCenter>
            ) : tabInvoice === "Proformas" ? (
              <TableInvoices
                columns={[
                  { header: "Dossier", accessor: "customerFile" },
                  { header: "Commenditaire", accessor: "entityName" },
                  { header: "Service", accessor: "nameDepartment" },
                  { header: "Date", accessor: "issueDate" },
                  { header: "HT", accessor: "amountExclVAT" },
                ]}
                data={sortedProformas}
              />
            ) : tabInvoice === "Avoirs" ? (
              <TableInvoices
                columns={[
                  { header: "N°", accessor: "invoiceNumber" },
                  { header: "Dossier", accessor: "nameCustomerFile" },
                  { header: "Service", accessor: "nameDepartment" },
                  { header: "Date", accessor: "issueDate" },
                  { header: "TTC", accessor: "totalAmount" },
                ]}
                data={sortedCreditNotes}
              />
            ) : tabInvoice === "Factures" ? (
              <TableInvoices
                columns={[
                  { header: "N°", accessor: "invoiceNumber" },
                  { header: "Dossier", accessor: "nameCustomerFile" },
                  { header: "Service", accessor: "nameDepartment" },
                  { header: "Date", accessor: "issueDate" },
                  { header: "HT", accessor: "amountExclVAT" },
                  { header: "TTC", accessor: "totalAmount" },
                  { header: "TVA", accessor: "amountVAT" },
                  { header: "Solde", accessor: "balance" },
                  { header: "Relance", accessor: "followUpDate" },
                  {
                    header: "Retard",
                    accessor: "daysAmountFromIssueDate",
                  },
                ]}
                data={sortedInvoices}
              />
            ) : null}

            {(tabInvoice === "Proformas" || tabInvoice === "Avoirs") && (
              <Totals
                data={
                  tabInvoice === "Proformas" ? sortedProformas : sortedCreditNotes
                }
              />
            )}
          </PagePanel>
        </>
      )}
    </PageShell>
  );
};

export default Invoices;
